<script setup lang="ts">
import SideModal from "~/components/Common/Modals/SideModal.vue";
import TabNavigationSimple from "~/components/Common/Navigations/TabNavigationSimple.vue";
import Loader from "~/components/Common/Loader.vue";
import { useAlertsStore } from "~/store/alerts/alerts.store";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";
import { AlertScope, AlertTab, AlertTabLabel } from "~/utils/enums/alerts.enums";
import type { IAlertCallbacks } from "~/utils/interfaces/alerts-interfaces";
import SvgIcon from "../Common/SvgIcon.vue";
import Tooltip from "../Common/Tooltip.vue";
import ConfirmDeleteModal from "../Common/Modals/ConfirmDeleteModal.vue";
import EmptyState from "../Common/EmptyState.vue";
import AlertCard from "./AlertCard.vue";

/**
 * Above the assistant panel (600), which is itself above the full-screen modals. The alert centre
 * is opened from the app header, so it has to stay reachable whatever is already floating.
 */
const HEADER_OVERLAY_Z_INDEX = 700;

const alertsPerPage = 20;

const props = withDefaults(
  defineProps<{
    callbacks: IAlertCallbacks;
    scope?: AlertScope;
  }>(),
  { scope: AlertScope.GLOBAL },
);

const alertsStore = useAlertsStore();
const scrollContainer = ref<HTMLElement | null>(null);
const offset = ref(0);
const total = ref(0);
const isLoading = ref(false);
const requestId = ref(0);
const hasMore = computed(() => alertsStore.alerts.length < total.value);

const tabs = [
  { name: AlertTabLabel[AlertTab.ACTIVE], filter: AlertTab.ACTIVE },
  { name: AlertTabLabel[AlertTab.ARCHIVED], filter: AlertTab.ARCHIVED },
];

const selectedTabLabel = computed(() => AlertTabLabel[alertsStore.selectedTab]);

const fetchAlerts = async (append = false) => {
  if (isLoading.value && append) return;
  isLoading.value = true;

  const currentRequestId = ++requestId.value;

  try {
    const response = await props.callbacks.fetchAlerts(
      alertsStore.selectedTab,
      append ? offset.value : 0,
      alertsPerPage,
    );

    if (currentRequestId !== requestId.value) return;

    if (append) {
      alertsStore.alerts.push(...response.data);
    } else {
      alertsStore.alerts = response.data;
    }

    total.value = response.total;
    offset.value = alertsStore.alerts.length;
  } finally {
    if (currentRequestId === requestId.value) {
      isLoading.value = false;
    }
  }
};

const resetAndFetch = async () => {
  offset.value = 0;
  total.value = 0;
  await fetchAlerts();
};

const onTabNavigate = (tab: Record<string, string>) => {
  alertsStore.selectedTab = tab.filter as AlertTab;
  resetAndFetch();
};

const refreshCounts = () => {
  alertsStore.fetchActiveAlertsCount();
  if (props.scope === AlertScope.SESSION) {
    const sessionDetailsStore = useSessionDetailsStore();
    const sessionId = sessionDetailsStore.sessionDetails?.sessionId;
    if (sessionId) alertsStore.fetchSessionAlertsCount(sessionId);
  }
};

const onArchive = async (alertId: string) => {
  await props.callbacks.archiveAlert(alertId);
  await resetAndFetch();
  refreshCounts();
};

const onReactivate = async (alertId: string) => {
  await props.callbacks.reactivateAlert(alertId);
  await resetAndFetch();
  refreshCounts();
};

const onConfirmArchiveAll = async () => {
  await props.callbacks.archiveAll();
  alertsStore.closeArchiveAllModal();
  await resetAndFetch();
  refreshCounts();
};

const getLink = (alert: (typeof alertsStore.alerts)[number]) => props.callbacks.getAlertLink?.(alert);

const handleScroll = () => {
  const el = scrollContainer.value;
  if (!el || isLoading.value || !hasMore.value) return;

  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
    fetchAlerts(true);
  }
};

onMounted(() => {
  resetAndFetch();
});
</script>

<template>
  <SideModal max-width="682px" :z-index="HEADER_OVERLAY_Z_INDEX" @hide="alertsStore.closePanel()">
    <template #title>
      <div class="flex gap-1 items-center">
        <span>Centre d'alertes</span>
        <Tooltip position="top-left" minified append-to-body :force-position-top="-5">
          <template #content>
            <div class="max-w-[420px] break-words">
              <span class="body_regular">Sessions dont vous êtes référent·e</span>
            </div>
          </template>
        </Tooltip>
      </div>
    </template>
    <template #content>
      <div class="flex justify-between border-b border-stroke-color">
        <TabNavigationSimple
          class="alert-tabs"
          :headers-nav="tabs"
          :tab-selected="selectedTabLabel"
          @tab-navigate="onTabNavigate"
        />
        <Tooltip
          v-if="alertsStore.selectedTab === AlertTab.ACTIVE && alertsStore.alerts.length"
          position="top-left"
          minified
          append-to-body
        >
          <template #img>
            <button class="button_secondary_default flex items-center gap-1" @click="alertsStore.openArchiveAllModal()">
              <SvgIcon name="archive" class="h-[16px] w-[16px]" />
              <span class="body_regular">Tout archiver</span>
            </button>
          </template>
          <template #content>
            <div class="max-w-[420px] break-words">
              <span class="body_regular">Archiver toutes les alertes actives</span>
            </div>
          </template>
        </Tooltip>
      </div>

      <div ref="scrollContainer" class="alert-list" @scroll="handleScroll">
        <template v-if="!alertsStore.alerts.length && !isLoading">
          <EmptyState
            v-if="alertsStore.selectedTab === AlertTab.ACTIVE"
            icon="tick"
            message="Aucune alerte active"
            size="medium"
          />
          <EmptyState
            v-else
            icon="tick"
            message="Les alertes archivées et résolues automatiquement apparaîtront ici"
            size="medium"
          />
        </template>
        <AlertCard
          v-for="alert in alertsStore.alerts"
          :key="alert.id"
          :alert="alert"
          :link="getLink(alert)"
          :scope="scope"
          @archive="onArchive"
          @reactivate="onReactivate"
        />
        <div v-if="isLoading" class="flex justify-center py-4">
          <Loader :width="24" :height="24" />
        </div>
      </div>
    </template>
  </SideModal>

  <ConfirmDeleteModal
    v-if="alertsStore.showArchiveAllModal"
    title="Archiver toutes les alertes ?"
    description="Vous pourrez les réactiver dans l'onglet Archivées"
    :consequences="[]"
    cancel-text="Annuler"
    confirm-text="Tout archiver"
    confirm-button-class="button__default"
    @cancel="alertsStore.closeArchiveAllModal()"
    @confirm="onConfirmArchiveAll"
  />
</template>

<style scoped>
:deep(.alert-tabs) {
  border-bottom: none;
}

.alert-list {
  @apply flex flex-col gap-[12px] pt-[16px] mb-[24px] overflow-y-auto;
  max-height: calc(100vh - 160px);
}
</style>
